import { inject, Injectable } from '@angular/core';
import {
  type CreateLearningResourceMicroLearningInput,
  type CreateLearningResourceMicrolearningMutation,
  type CreateLearningResourceMicrolearningMutationVariables,
  type DestroyLearningResourceMicroLearningInput,
  type DestroyLearningResourceMicroLearningMutation,
  type DestroyLearningResourceMicroLearningMutationVariables,
  type LearningResourceMicroLearningQuery,
  type LearningResourceMicroLearningQueryVariables,
  type UpdateLearningResourceMicroLearningInput,
  type UpdateLearningResourceMicroLearningMutation,
  type UpdateLearningResourceMicroLearningMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode, assertType } from '../../../utils/type-helpers';
import { learningResourceMicroLearningMinimal } from '../learning-resource-micro-learning.minimal';
import { learningResourceSetQuery } from './shared-learning-resource.api';

export const learningResourceMicrolearningQuery = gql`
  query LearningResourceMicroLearning($learningResourceMicroLearningId: ID!) {
    node(id: $learningResourceMicroLearningId) {
      ... on LearningResourceMicroLearning {
        ...LearningResourceMicroLearningMinimal

        microLearningSteps {
          nodes {
            id
            title
            descriptionJsonContent
            image {
              ...AttachedFile
            }
            link
            linkButtonText
            order
          }
        }
      }
    }
  }
  ${learningResourceMicroLearningMinimal}
`;

@Injectable({
  providedIn: 'root',
})
export class SharedLearningResourceMicroLearningApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceMicrolearning(learningResourceMicroLearningId: string) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceMicroLearningQuery, LearningResourceMicroLearningQueryVariables>({
        query: learningResourceMicrolearningQuery,
        variables: {
          learningResourceMicroLearningId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceMicroLearning')));
  }

  createLearningResourceMicrolearning(input: CreateLearningResourceMicroLearningInput) {
    return this.sharedGraphQlService
      .mutateV2<CreateLearningResourceMicrolearningMutation, CreateLearningResourceMicrolearningMutationVariables>({
        mutation: gql`
          mutation CreateLearningResourceMicrolearning($input: CreateLearningResourceMicroLearningInput!) {
            createLearningResourceMicroLearning(input: $input) {
              errors {
                message
                path
              }
              learningResourceMicroLearning {
                ...LearningResourceMicroLearningMinimal
              }
            }
          }
          ${learningResourceMicroLearningMinimal}
        `,
        variables: { input },
        refetchQueries: [
          {
            query: learningResourceSetQuery,
            variables: { learningResourceSetId: input.learningResourceSetId },
          },
        ],
      })
      .pipe(
        map((result) =>
          assertType(
            result.data?.createLearningResourceMicroLearning?.learningResourceMicroLearning,
            'LearningResourceMicroLearning',
          ),
        ),
      );
  }

  updateLearningResourceMicrolearning(input: UpdateLearningResourceMicroLearningInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<UpdateLearningResourceMicroLearningMutation, UpdateLearningResourceMicroLearningMutationVariables>({
          mutation: gql`
            mutation UpdateLearningResourceMicroLearning($input: UpdateLearningResourceMicroLearningInput!) {
              updateLearningResourceMicroLearning(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceMicroLearning {
                  ...LearningResourceMicroLearningMinimal
                }
              }
            }
            ${learningResourceMicroLearningMinimal}
          `,
          variables: { input },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceMicroLearning.learningResourceMicroLearning))
    );
  }

  destroyLearningResourceMicroLearning(
    input: DestroyLearningResourceMicroLearningInput,
    learningResourceSetId: string,
  ) {
    return this.sharedGraphQlService.mutateV2<
      DestroyLearningResourceMicroLearningMutation,
      DestroyLearningResourceMicroLearningMutationVariables
    >({
      mutation: gql`
        mutation DestroyLearningResourceMicroLearning($input: DestroyLearningResourceMicroLearningInput!) {
          destroyLearningResourceMicroLearning(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId },
        },
      ],
    });
  }
}
